import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import AlertMessage from "../../components/Common/AlertMessage";
import extractApiErrors from "../../utils/extractApiErrors";
import {
  createCompany,
  updateCompany,
  getCompanyById,
} from "../../services/companyService";

import {
  Container,
  Card,
  Form,
  InputGroup,
  Row,
  Col,
  Badge,
  Button,
} from "react-bootstrap";

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("El nombre es obligatorio")
    .min(4, "Debe tener al menos 4 caracteres")
    .test(
      "no-leading-trailing-spaces",
      "El nombre no debe tener espacios al inicio o al final.",
      (value) => value?.trim() === value
    ),
  legalName: Yup.string()
    .required("La razón social es obligatoria")
    .min(4, "Debe tener al menos 4 caracteres")
    .test(
      "no-leading-trailing-spaces",
      "La razón social no debe tener espacios al inicio o al final.",
      (value) => value?.trim() === value
    ),
  email: Yup.string()
    .required("El correo es obligatorio")
    .test(
      "no-leading-trailing-spaces",
      "El correo no debe tener espacios al inicio o al final.",
      (value) => value?.trim() === value
    )
    .email("Correo inválido"),
  nit: Yup.string()
    .required("El NIT es obligatorio")
    .matches(
      /^\d{8,11}$/,
      "El NIT debe contener entre 8 y 11 dígitos numéricos"
    ),
  phoneNumber: Yup.string()
    .required("El número de celular es obligatorio")
    .matches(
      /^3\d{9,14}$/,
      "El número de celular debe iniciar en 3 y tener entre 10 y 15 dígitos"
    ),
  isActive: Yup.boolean().required(),
});

const CompanyForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [errors, setErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      legalName: "",
      email: "",
      nit: "",
      phoneNumber: "",
      isActive: true,
    },
    validationSchema,
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      setErrors([]);
      setSuccessMessage(null);

      try {
        const payload = {
          name: values.name?.trim(),
          legalName: values.legalName?.trim(),
          email: values.email?.trim(),
          nit: values.nit,
          phoneNumber: values.phoneNumber,
          isActive: values.isActive,
        };

        let response;

        if (isEdit) {
          response = await updateCompany({ companyId: id, ...payload });
          if (!response?.success) throw { backend: response };
          setSuccessMessage("✅ Empresa actualizada correctamente.");
        } else {
          response = await createCompany(payload);
          if (!response?.success) throw { backend: response };
          setSuccessMessage("✅ Empresa registrada correctamente.");
        }

        Swal.fire({
          toast: true,
          position: "top",
          icon: "success",
          title: isEdit ? "Cambios guardados" : "Empresa creada",
          timer: 1600,
          showConfirmButton: false,
        });
      } catch (error) {
        setErrors(extractApiErrors(error));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const statusBadge = useMemo(() => {
    return formik.values.isActive ? (
      <Badge bg="success" className="px-2 py-1" style={{ fontWeight: 700 }}>
        Activa
      </Badge>
    ) : (
      <Badge bg="secondary" className="px-2 py-1" style={{ fontWeight: 700 }}>
        Inactiva
      </Badge>
    );
  }, [formik.values.isActive]);

  // Cargar datos al editar
  useEffect(() => {
    if (!isEdit) return;

    setLoadingCompany(true);

    getCompanyById(id)
      .then((response) => {
        const d = response?.data;

        formik.setValues({
          name: d?.name || "",
          legalName: d?.legalName || "",
          email: d?.email || "",
          nit: d?.nit || "",
          phoneNumber: d?.phoneNumber || "",
          isActive: d?.isActive ?? true,
        });
      })
      .catch(() => {
        Swal.fire("Error", "No se pudo cargar la empresa", "error");
        navigate("/dashboard/company");
      })
      .finally(() => setLoadingCompany(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Redirección tras guardar
  useEffect(() => {
    if (!successMessage) return;

    const timeout = setTimeout(() => {
      navigate("/dashboard/company");
    }, 900);

    return () => clearTimeout(timeout);
  }, [successMessage, navigate]);

  const isInvalid = (name) => formik.touched[name] && !!formik.errors[name];
  const errorMsg = (name) => (formik.touched[name] ? formik.errors[name] : "");

  return (
    <Container className="mt-4">
      <Card className="shadow-sm novian-form">
        {/* Header bonito */}
        <Card.Header className="bg-body border-0 pb-0">
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
            <div className="min-w-0">
              <h4 className="mb-1">
                {isEdit ? "Editar empresa" : "Crear nueva empresa"}
              </h4>
              <div className="text-muted small">
                {isEdit
                  ? "Actualiza la información. El estado solo cambia si tú lo cambias."
                  : "Registra una empresa para usarla en el sistema."}
              </div>
            </div>

            {/* Estado solo en edición (para no ensuciar el create) */}
            {isEdit && (
              <div className="d-flex align-items-center gap-2">
                {statusBadge}

                {/* Toggle discreto: checkbox “pill” */}
                <Form.Check
                  type="checkbox"
                  id="isActive"
                  className="mb-0"
                  label=""
                  checked={formik.values.isActive}
                  onChange={(e) =>
                    formik.setFieldValue("isActive", e.target.checked, true)
                  }
                  style={{ transform: "scale(1.05)" }}
                  title="Cambiar estado"
                />
              </div>
            )}
          </div>
        </Card.Header>

        <Card.Body className="pt-3">
          <Form noValidate onSubmit={formik.handleSubmit}>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group controlId="name">
                  <Form.Label>Nombre *</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={isInvalid("name")}
                      disabled={loadingCompany}
                      placeholder="Ej: Novian Legal"
                      maxLength={150}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errorMsg("name")}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group controlId="legalName">
                  <Form.Label>Razón social *</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      type="text"
                      name="legalName"
                      value={formik.values.legalName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={isInvalid("legalName")}
                      disabled={loadingCompany}
                      placeholder="Ej: Novian Legal S.A.S."
                      maxLength={200}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errorMsg("legalName")}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group controlId="email">
                  <Form.Label>Correo electrónico *</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={isInvalid("email")}
                      disabled={loadingCompany}
                      placeholder="contacto@empresa.com"
                      maxLength={150}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errorMsg("email")}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="nit">
                  <Form.Label>NIT *</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      maxLength={11}
                      name="nit"
                      value={formik.values.nit}
                      onChange={(e) => {
                        const onlyDigits = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 11);
                        formik.setFieldValue("nit", onlyDigits, true);
                      }}
                      onBlur={formik.handleBlur}
                      isInvalid={isInvalid("nit")}
                      disabled={loadingCompany}
                      placeholder="900123456"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errorMsg("nit")}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="phoneNumber">
                  <Form.Label>Número de celular *</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      maxLength={15}
                      name="phoneNumber"
                      value={formik.values.phoneNumber}
                      onChange={(e) => {
                        const onlyDigits = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 15);
                        formik.setFieldValue("phoneNumber", onlyDigits, true);
                      }}
                      onBlur={formik.handleBlur}
                      isInvalid={isInvalid("phoneNumber")}
                      disabled={loadingCompany}
                      placeholder="3001234567"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errorMsg("phoneNumber")}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            {/* Alertas backend */}
            <div className="mt-3">
              {errors.length > 0 && (
                <AlertMessage
                  type="error"
                  message={
                    <ul className="mb-0">
                      {errors.map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  }
                  onClose={() => setErrors([])}
                />
              )}

              {successMessage && (
                <AlertMessage
                  type="success"
                  message={successMessage}
                  onClose={() => setSuccessMessage(null)}
                />
              )}
            </div>

            {/* Botones */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <Button
                variant="outline-secondary"
                type="button"
                onClick={() => navigate("/dashboard/company")}
                disabled={formik.isSubmitting}
              >
                Volver
              </Button>

              <Button
                variant="primary"
                type="submit"
                disabled={formik.isSubmitting || loadingCompany}
              >
                {formik.isSubmitting
                  ? "Guardando..."
                  : isEdit
                  ? "Guardar cambios"
                  : "Crear empresa"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CompanyForm;
